import type { ModifiableElementType } from "schemas/generated/fileModifiers";

import React from "react";

import { Tag } from "@replo/design-system/components/tag/Tag";
import {
  Link,
  LucideImagePlay,
  ShoppingBag,
  Sparkles,
  SquareDashed,
  Type,
  Zap,
} from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface ElementTypeTagProps {
  elementType?: ModifiableElementType;
  showRemoveOnHover?: boolean;
  onRemove?: () => void;
}

const ElementTypeTag: React.FC<ElementTypeTagProps> = ({
  elementType,
  showRemoveOnHover = false,
  onRemove,
}) => {
  const [isHover, setIsHover] = React.useState(false);
  if (!elementType) {
    return null;
  }

  const getElementTagDisplay = (type: ModifiableElementType) => {
    return exhaustiveSwitch({ type })({
      text: () => ({ IconComponent: Type, label: "Text" }),
      container: () => ({ IconComponent: SquareDashed, label: "Container" }),
      image: () => ({ IconComponent: LucideImagePlay, label: "Media" }),
      video: () => ({ IconComponent: LucideImagePlay, label: "Media" }),
      "product-section": () => ({
        IconComponent: ShoppingBag,
        label: "Buy Box",
      }),
      button: () => ({ IconComponent: Zap, label: "Button" }),
      icon: () => ({ IconComponent: Sparkles, label: "Icon" }),
      unknown: () => ({ IconComponent: SquareDashed, label: "Unknown" }),
      link: () => ({ IconComponent: Link, label: "Link" }),
    });
  };

  const { IconComponent, label } = getElementTagDisplay(elementType);

  const StartEnhancer =
    isHover && showRemoveOnHover ? (
      <button
        type="button"
        aria-label="Clear selection"
        className="cursor-pointer inline-flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.();
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    ) : (
      <IconComponent size={14} />
    );

  return (
    <div
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <Tag size="base" variant="info" startEnhancer={StartEnhancer}>
        {label}
      </Tag>
    </div>
  );
};

export default ElementTypeTag;
